import { useEffect, useRef, useState } from "react";
import apiClient from "../../services/api";
import { getInit, relTime } from "../../utils";
import {
  Button,
  DialogPrimitives,
  FieldGroup,
  FieldLabel,
  Input,
} from "../../shared/ui";

const NOTIF_TYPE_STYLE = {
  MENTION: { bg: "rgba(64,117,193,.12)", color: "#4075C1" },
  TASK_ASSIGNED: { bg: "rgba(16,185,129,.12)", color: "#059669" },
  TASK_UPDATED: { bg: "rgba(245,158,11,.12)", color: "#d97706" },
  PROJECT_ALERT: { bg: "rgba(239,68,68,.12)", color: "#dc2626" },
  REPORT_READY: { bg: "rgba(168,85,247,.12)", color: "#9333ea" },
  SYSTEM: { bg: "rgba(107,107,107,.1)", color: "#6b6b6b" },
  mention: { bg: "rgba(64,117,193,.12)", color: "#4075C1" },
  report: { bg: "rgba(168,85,247,.12)", color: "#9333ea" },
  note: { bg: "rgba(16,185,129,.12)", color: "#059669" },
};

const ROLE_LABEL = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  MANAGER: "Manager",
  CONSULTANT: "Consultant",
  CLIENT_VIEW: "Client View",
};

function AvatarBadge({ initials, src, size = 30, className = "", style = {} }) {
  const avatarClass = className ? `avatar ${className}` : "avatar";
  const fallbackStyle = {
    width: size,
    height: size,
    fontSize: size * 0.34,
    ...style,
  };

  if (src) {
    return (
      <>
        <img
          src={src}
          alt={initials}
          className={avatarClass}
          style={{ width: size, height: size, objectFit: "cover", flexShrink: 0, ...style }}
          onError={(event) => {
            event.currentTarget.style.display = "none";
            event.currentTarget.nextSibling.style.display = "flex";
          }}
        />
        <div className={avatarClass} style={{ display: "none", ...fallbackStyle }}>{initials}</div>
      </>
    );
  }

  return <div className={avatarClass} style={fallbackStyle}>{initials}</div>;
}

export function NotificationBell({ notifications, onMarkRead, onMarkAllRead, onClearAll, onOpenMentions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const unread = notifications.filter((notification) => !notification.read).length;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  function handleOpen() {
    setOpen((current) => !current);
    if (!open && unread > 0) {
      onMarkAllRead();
    }
  }

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="notif-btn" title="Notifications" onClick={handleOpen}>
        🔔
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel">
          <div className="notif-head">
            <span className="notif-head-title">Notifications{unread > 0 ? ` · ${unread} new` : ""}</span>
            <div className="notif-head-actions">
              <span className="notif-head-link" onClick={() => { onOpenMentions?.(); setOpen(false); }}>Mentions</span>
              <span className="notif-head-clear" onClick={() => { onClearAll(); setOpen(false); }}>Clear all</span>
            </div>
          </div>
          <div className="notif-list">
            {notifications.length === 0 && <div className="notif-empty">No notifications yet</div>}
            {notifications.map((notification) => {
              const timestamp = notification.createdAt || notification.ts;
              const typeKey = (notification.type || "").toUpperCase();
              const style = NOTIF_TYPE_STYLE[typeKey] || NOTIF_TYPE_STYLE[notification.type] || NOTIF_TYPE_STYLE.SYSTEM;
              return (
                <div
                  key={notification.id}
                  className={`notif-item${notification.read ? "" : " unread"}`}
                  onClick={() => onMarkRead(notification.id)}
                >
                  {notification.title && <div className="notif-title">{notification.title}</div>}
                  <div className="notif-text">{notification.text}</div>
                  <div className="notif-meta">
                    <span className="notif-type" style={{ background: style.bg, color: style.color }}>
                      {(notification.type || "").replace(/_/g, " ").toLowerCase()}
                    </span>
                    {timestamp && <span>{relTime(timestamp)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProfileModal({ user: initialUser, onClose, onSave, onLogout }) {
  const [profile, setProfile] = useState(initialUser);
  const [name, setName] = useState(initialUser?.name || "");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.getCurrentUser()
      .then((data) => {
        const user = data.user || data;
        setProfile(user);
        setName(user.name || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    if (!name.trim() || name.trim() === profile?.name) {
      onClose();
      return;
    }

    setSaving(true);
    await onSave(name.trim());
    setSaving(false);
    onClose();
  }

  const currentUser = profile || initialUser;
  const roleLabel = ROLE_LABEL[currentUser?.role] || currentUser?.role || "";
  const memberSince = currentUser?.createdAt
    ? new Date(currentUser.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;
  const lastLogin = currentUser?.lastLoginAt
    ? new Date(currentUser.lastLoginAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <DialogPrimitives.Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitives.DialogContent className="max-w-[440px]">
        {/* Header — avatar + name + role badge + member-since. Replaces
            the profile-header CSS block with inline utilities. */}
        <div className="flex flex-col items-center text-center pb-4 mb-4 border-b border-rc-border">
          <div className="w-20 h-20 rounded-full overflow-hidden mb-3 ring-2 ring-rc-border">
            {currentUser?.avatarUrl ? (
              <img src={currentUser.avatarUrl} alt={currentUser?.name} className="w-full h-full object-cover" />
            ) : (
              <AvatarBadge
                initials={currentUser?.initials || getInit(currentUser?.name || "")}
                size={80}
              />
            )}
          </div>
          {loading ? (
            <div className="text-xs text-rc-text-secondary">Loading profile...</div>
          ) : (
            <>
              <DialogPrimitives.DialogTitle className="text-base font-bold text-rc-text-primary">
                {currentUser?.name}
              </DialogPrimitives.DialogTitle>
              <div className="text-xs text-rc-text-secondary mt-0.5">{currentUser?.email}</div>
              <span className="mt-2 inline-flex items-center rounded-full bg-[rgba(59,127,250,0.12)] text-rc-accent-strong px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em]">
                {roleLabel}
              </span>
              {memberSince && (
                <div className="text-[10.5px] text-rc-text-dim mt-2">Member since {memberSince}</div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <FieldGroup>
            <FieldLabel>Display Name</FieldLabel>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Your full name" disabled={loading} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Email address</FieldLabel>
            <Input value={currentUser?.email || ""} readOnly />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup>
              <FieldLabel>Role</FieldLabel>
              <Input value={roleLabel} readOnly />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Last login</FieldLabel>
              <Input value={lastLogin || "-"} readOnly />
            </FieldGroup>
          </div>
        </div>

        <DialogPrimitives.DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogPrimitives.DialogFooter>

        <div className="mt-3 pt-3 border-t border-rc-border flex justify-center">
          <Button variant="destructive" size="sm" onClick={onLogout}>Logout</Button>
        </div>
      </DialogPrimitives.DialogContent>
    </DialogPrimitives.Dialog>
  );
}
